import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone, MapPin, Check } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import BackgroundWord from "./BackgroundWords";
import { supabase, INQUIRY_TYPES, formatSupabaseError } from "../lib/supabase";
import { useContent } from "../context/ContentContext";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  artist_name: "",
  inquiry_type: "",
  message: "",
  consent: false,
};

export const Contact = () => {
  const { content } = useContent();
  const c = content?.contact || {};
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.inquiry_type || !form.message) {
      toast.error("Bitte alle Pflichtfelder ausfüllen.");
      return;
    }
    if (form.message.length < 100) {
      toast.error(`Nachricht muss mindestens 100 Zeichen enthalten (aktuell: ${form.message.length}).`);
      return;
    }
    if (!form.consent) {
      toast.error("Bitte DSGVO-Einwilligung bestätigen.");
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.from("contact_submissions").insert([
        {
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          artist_name: form.artist_name || null,
          inquiry_type: form.inquiry_type,
          message: form.message,
          consent: true,
        },
      ]);
      if (error) throw error;
      toast.success("Bewerbung gesendet. Wir melden uns innerhalb 48h.");
      setForm(initialForm);
    } catch (err) {
      toast.error(formatSupabaseError(err, "Übertragung fehlgeschlagen."));
    } finally {
      setSubmitting(false);
    }
  };

  const email = c.email || "info@strassenwert.de";
  const phone = c.phone || "+49 175 50 03 19 2";
  const phoneLink = c.phone_link || "+491755003192";

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative bg-[#050505] py-32 md:py-44 overflow-hidden grain"
    >
      <BackgroundWord word="Kontakt" />

      <div className="absolute -top-40 -right-40 w-[40rem] h-[40rem] rounded-full bg-[#ff0033]/15 blur-[140px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-16 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-3 mb-6 text-[11px] tracking-[0.4em] uppercase text-zinc-500 font-semibold">
              <span className="block w-10 h-px bg-[#ff0033] shadow-[0_0_10px_#ff0033]" />
              06 — Bewerbung & Kontakt
            </div>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-6xl">
              Schreib uns. <br />
              <span className="text-glow-red text-white">Wir hören zu.</span>
            </h2>

            <p className="mt-8 text-zinc-400 leading-relaxed max-w-md">
              {c.intro ||
                "Bewerbung, Booking, Kollaboration oder einfach ein erstes Gespräch — wähl die Anfrageart aus dem Dropdown und schick uns ein paar Sätze. Antwort innerhalb 48 Stunden."}
            </p>

            <div className="mt-12 space-y-5">
              <div className="flex items-center gap-4 text-sm text-zinc-300">
                <Mail size={16} className="text-[#ff0033]" />
                <a
                  href={`mailto:${email}`}
                  data-testid="contact-email"
                  className="hover:text-white transition-colors"
                >
                  {email}
                </a>
                <span className="text-[9px] tracking-[0.3em] uppercase text-zinc-600 font-bold">Allgemein</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-zinc-300">
                <Mail size={16} className="text-[#ff0033]" />
                <a
                  href="mailto:management@strassenwert.de"
                  className="hover:text-white transition-colors"
                >
                  management@strassenwert.de
                </a>
                <span className="text-[9px] tracking-[0.3em] uppercase text-zinc-600 font-bold">Management</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-zinc-300">
                <Mail size={16} className="text-[#ff0033]" />
                <a
                  href="mailto:studio@strassenwert.de"
                  className="hover:text-white transition-colors"
                >
                  studio@strassenwert.de
                </a>
                <span className="text-[9px] tracking-[0.3em] uppercase text-zinc-600 font-bold">Studio</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-zinc-300">
                <Phone size={16} className="text-[#ff0033]" />
                <a
                  href={`tel:${phoneLink}`}
                  className="hover:text-white transition-colors"
                >
                  {phone}
                </a>
              </div>
              <div className="flex items-center gap-4 text-sm text-zinc-300">
                <MapPin size={16} className="text-[#ff0033]" />
                <span>{c.location || "Berlin · Studios im Aufbau"}</span>
              </div>
            </div>
          </motion.div>

          <motion.form
            data-testid="contact-form"
            noValidate
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="lg:col-span-7 bg-[#0A0A0A] border border-white/[0.06] p-8 md:p-12 relative"
          >
            <div className="absolute top-5 right-5 red-led" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-8">
              <Field label="Name *">
                <input
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={handleChange("name")}
                  required
                  className="contact-input"
                  placeholder="Max Mustermann"
                />
              </Field>

              <Field label="Künstlername">
                <input
                  data-testid="contact-input-artist"
                  value={form.artist_name}
                  onChange={handleChange("artist_name")}
                  className="contact-input"
                  placeholder="Optional"
                />
              </Field>

              <Field label="E-Mail *">
                <input
                  data-testid="contact-input-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange("email")}
                  className="contact-input"
                  placeholder="name@email.com"
                />
              </Field>

              <Field label="Telefon">
                <input
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={handleChange("phone")}
                  className="contact-input"
                  placeholder="+49 ..."
                />
              </Field>

              <Field label="Anfrageart *" full>
                <Select
                  value={form.inquiry_type}
                  onValueChange={(v) => setForm((f) => ({ ...f, inquiry_type: v }))}
                >
                  <SelectTrigger
                    data-testid="contact-select-trigger"
                    className="bg-transparent border-0 border-b border-white/15 rounded-none h-12 px-0 text-white font-medium tracking-wide focus:ring-0 focus:border-[#ff0033]/60 hover:border-white/30 transition-colors"
                  >
                    <SelectValue placeholder="Anfrageart auswählen…" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border border-white/10 text-white">
                    {INQUIRY_TYPES.map((t) => (
                      <SelectItem
                        key={t.value}
                        value={t.value}
                        data-testid={`option-${t.value}`}
                        className="focus:bg-[#ff0033]/15 focus:text-white"
                      >
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Nachricht *" full>
                <textarea
                  data-testid="contact-input-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={handleChange("message")}
                  className="contact-input resize-none"
                  placeholder="Was möchtest du uns sagen? Demo-Links, Vision, Ziel — alles willkommen. (Mindestens 100 Zeichen)"
                />
                <div
                  data-testid="contact-message-counter"
                  className={`mt-2 text-[10px] tracking-[0.25em] uppercase font-bold ${
                    form.message.length >= 100 ? "text-[#ff0033]" : "text-zinc-500"
                  }`}
                >
                  {form.message.length} / 100 Zeichen
                  {form.message.length < 100 && (
                    <span className="ml-2 text-zinc-600 normal-case tracking-normal font-medium">
                      (noch {100 - form.message.length} nötig)
                    </span>
                  )}
                </div>
              </Field>
            </div>

            {/* DSGVO Consent */}
            <label
              className="mt-8 flex items-start gap-4 cursor-pointer group"
              data-testid="contact-consent"
            >
              <span
                className={`relative shrink-0 mt-0.5 flex items-center justify-center w-5 h-5 border transition-all ${
                  form.consent
                    ? "bg-[#ff0033] border-[#ff0033] shadow-[0_0_12px_rgba(255,0,51,0.5)]"
                    : "bg-transparent border-white/20 group-hover:border-white/50"
                }`}
              >
                {form.consent && <Check size={12} className="text-white" strokeWidth={3} />}
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                  className="sr-only"
                  data-testid="contact-consent-checkbox"
                />
              </span>
              <span className="text-xs text-zinc-400 leading-relaxed max-w-2xl">
                Ich habe die{" "}
                <Link
                  to="/datenschutz"
                  target="_blank"
                  className="text-white underline decoration-[#ff0033]/60 hover:decoration-[#ff0033] underline-offset-2"
                >
                  Datenschutzerklärung
                </Link>{" "}
                gelesen und stimme der Verarbeitung meiner Daten zur Bearbeitung meiner
                Anfrage zu. Ich kann meine Einwilligung jederzeit per E-Mail an{" "}
                <a
                  href={`mailto:${email}`}
                  className="text-white underline decoration-[#ff0033]/60 underline-offset-2"
                >
                  {email}
                </a>{" "}
                widerrufen. *
              </span>
            </label>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-8">
              <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-600">
                Wir melden uns innerhalb 48h
              </span>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={submitting}
                className="group relative inline-flex items-center gap-3 bg-white text-black px-7 py-4 text-[11px] font-bold tracking-[0.3em] uppercase overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-[0_0_30px_rgba(255,0,51,0.4)] transition-all"
              >
                <span className="relative z-10 transition-colors group-hover:text-white">
                  {submitting ? "Sende…" : "Bewerbung senden"}
                </span>
                <ArrowUpRight
                  size={14}
                  className="relative z-10 group-hover:text-white group-hover:rotate-45 transition-all duration-500"
                />
                <span className="absolute inset-0 bg-[#ff0033] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
              </button>
            </div>
          </motion.form>
        </div>
      </div>

      <style>{`
        .contact-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid rgba(255,255,255,0.15);
          padding: 12px 0;
          color: #fff;
          font-size: 15px;
          font-family: 'Manrope', sans-serif;
          font-weight: 500;
          outline: none;
          transition: border-color .3s, box-shadow .3s;
        }
        .contact-input::placeholder { color: #52525b; }
        .contact-input:focus {
          border-color: rgba(255,0,51,0.6);
          box-shadow: 0 10px 20px -10px rgba(255,0,51,0.25);
        }
      `}</style>
    </section>
  );
};

const Field = ({ label, full, children }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <span className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">
      {label}
    </span>
    {children}
  </label>
);

export default Contact;
